// Talmia 2.0 · Data Lab data audit primitive.
// Reveals every field on a document, grouped into collapsible sections by
// top-level object key. No mutual recursion — traversal happens up-front,
// yielding a flat list of {section, rows} entries that render simply.
import { Fragment, useMemo, useState } from "react";
import { ChevronRight, ChevronDown, Database, Search, Copy as CopyIcon } from "lucide-react";

/* Prettify a key path segment. */
function humanize(key) {
  if (!key) return "";
  return String(key)
    .replace(/^_+/, "")
    .replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

/* Friendly labels for known top-level containers. */
const TITLES = {
  BCProduct:  "BigCommerce · Product",
  TMProduct:  "Talmia · Product",
  BCCustomer: "BigCommerce · Customer",
  BCOrder:    "BigCommerce · Order",
  JCCall:     "JustCall · Payload",
  AITags:     "Talmia AI · Tags",
  _lab:       "Data Lab · Computed",
};

function labelFor(path) {
  if (TITLES[path]) return TITLES[path];
  // Nested marketplace listings under TMProduct
  if (path === "TMProduct.OBListing") return "OnBuy · Listing";
  if (path === "TMProduct.AZListing") return "Amazon · Listing";
  if (path === "TMProduct.EBListing") return "eBay · Listing";
  if (path.startsWith("JCCall.data")) return "JustCall · Data";
  return humanize(path.split(".").pop());
}

function isSkipped(path, skip) {
  if (!skip?.length) return false;
  for (const s of skip) {
    if (path === s || path.startsWith(s + ".")) return true;
  }
  return false;
}

/* Classify a scalar value for the quality X-ray:
   empty | moneyString (£/comma but numeric) | nonIsoDate (date-ish, not ISO). */
function flagsFor(v) {
  if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return { empty: true };
  if (typeof v === "string") {
    const s = v.trim();
    if (/[£$]/.test(s) || (/,/.test(s) && /^-?[\d,]+(\.\d+)?$/.test(s))) {
      const n = Number(s.replace(/[£$,]/g, ""));
      if (!Number.isNaN(n)) return { moneyString: true };
    }
    const isIso = /^\d{4}-\d{2}-\d{2}[T ]/.test(s);
    const hasName = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|Mon|Tue|Wed|Thu|Fri|Sat|Sun)/.test(s);
    if (!isIso && hasName && /\b(19|20)\d{2}\b/.test(s) && !Number.isNaN(Date.parse(s))) {
      return { nonIsoDate: true };
    }
  }
  return {};
}

function hasTypeIssue(f) {
  return !!(f && (f.moneyString || f.nonIsoDate));
}

/* Traverse an object → list of sections. Each section = { path, title, rows }
   where rows = [ { key, path, value } ] for scalars + arrays only.
   Nested plain-object children become their own section entries. */
function buildSections(root, skip = []) {
  const sections = [];
  const stack = [{ obj: root, prefix: "" }];
  while (stack.length) {
    const { obj, prefix } = stack.shift();
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) continue;
    const rows = [];
    for (const [k, v] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${k}` : k;
      if (isSkipped(path, skip)) continue;
      if (v && typeof v === "object" && !Array.isArray(v)) {
        if (Object.keys(v).length > 0) stack.push({ obj: v, prefix: path });
      } else {
        rows.push({ key: k, path, value: v, flags: flagsFor(v) });
      }
    }
    if (rows.length) {
      sections.push({
        path: prefix || "__top",
        title: prefix ? labelFor(prefix) : "Top-level",
        rows,
      });
    }
  }
  return sections;
}

/* One scalar / array value cell. */
function Value({ value }) {
  if (value == null || value === "") {
    return <span className="t2-mono" style={{ color: "var(--t2-grey4)" }}>—</span>;
  }
  if (typeof value === "boolean") {
    return (
      <span className="t2-mono" style={{
        color: value ? "var(--t2-ok)" : "var(--t2-grey4)",
        fontWeight: 700, fontSize: 11,
      }}>{value ? "TRUE" : "FALSE"}</span>
    );
  }
  if (typeof value === "number") {
    return (
      <span className="t2-mono" style={{ color: "var(--t2-white)", fontVariantNumeric: "tabular-nums" }}>
        {Number.isInteger(value) ? value : value.toFixed(2)}
      </span>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="t2-mono" style={{ color: "var(--t2-grey4)", fontSize: 11 }}>empty list</span>;
    }
    const scalar = typeof value[0] !== "object" || value[0] == null;
    return (
      <details>
        <summary className="t2-mono" style={{
          background: "var(--t2-surface3)", border: "1px solid var(--t2-border2)",
          borderRadius: 6, padding: "3px 8px", color: "var(--t2-orange)",
          fontSize: 10.5, fontWeight: 700, cursor: "pointer",
          display: "inline-block",
        }}>
          {value.length} item{value.length === 1 ? "" : "s"}
        </summary>
        <div style={{
          marginTop: 6, padding: 8,
          background: "var(--t2-surface2)", border: "1px solid var(--t2-border)", borderRadius: 6,
          maxHeight: 240, overflowY: "auto",
        }} className="t2-scroll">
          {scalar ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {value.slice(0, 60).map((v, i) => (
                <span key={i} className="t2-mono" style={{
                  padding: "2px 7px", borderRadius: 999,
                  background: "var(--t2-surface3)", color: "var(--t2-grey1)",
                  fontSize: 10.5,
                }}>{String(v)}</span>
              ))}
            </div>
          ) : (
            <pre className="t2-mono" style={{
              fontSize: 10.5, color: "var(--t2-grey1)", lineHeight: 1.4,
              whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0,
            }}>
              {JSON.stringify(value.slice(0, 20), null, 2)}
            </pre>
          )}
        </div>
      </details>
    );
  }
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    try {
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) {
        return <span className="t2-mono" style={{ color: "var(--t2-teal)", fontSize: 11 }}>
          {d.toLocaleString("en-GB")}
        </span>;
      }
    } catch { /* ignore */ }
  }
  if (/^https?:\/\//.test(s)) {
    return <a href={s} target="_blank" rel="noreferrer" className="t2-mono"
      style={{
        color: "var(--t2-orange)", fontSize: 11, textDecoration: "underline",
        maxWidth: 320, display: "inline-block", overflow: "hidden",
        textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom",
      }}>
      {s}
    </a>;
  }
  return (
    <span title={s.length > 140 ? s : undefined} style={{
      color: "var(--t2-grey1)", fontSize: 12, display: "block",
      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: s.length > 140 ? "nowrap" : "normal",
    }}>{s}</span>
  );
}

/* One section of rows. */
function Section({ section, needle, defaultOpen, issuesOnly }) {
  const [open, setOpen] = useState(defaultOpen);
  let rows = needle
    ? section.rows.filter((r) =>
        r.key.toLowerCase().includes(needle) ||
        String(r.value ?? "").toLowerCase().includes(needle))
    : section.rows;
  if (issuesOnly) rows = rows.filter((r) => hasTypeIssue(r.flags));
  if ((needle || issuesOnly) && rows.length === 0) return null;
  return (
    <div style={{ marginTop: 10, paddingLeft: 8, borderLeft: "2px solid var(--t2-border2)" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="t2-mono"
        data-testid={`audit-section-${section.path.replace(/\./g, "-")}`}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          background: "none", border: "none", padding: "4px 0",
          fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase",
          color: open ? "var(--t2-orange)" : "var(--t2-brass)",
          cursor: "pointer", fontWeight: 700,
        }}
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {section.title}
        <span className="t2-mono" style={{
          color: "var(--t2-grey4)", fontWeight: 400, fontSize: 10,
        }}>· {rows.length} field{rows.length === 1 ? "" : "s"}</span>
      </button>
      {open && (
        <div style={{
          marginTop: 6,
          display: "grid",
          gridTemplateColumns: "minmax(160px, 220px) 1fr",
          gap: "4px 12px",
          fontSize: 12,
        }}>
          {rows.map((r) => (
            <Fragment key={r.path}>
              <div className="t2-mono" title={r.path} style={{
                color: "var(--t2-brass)", fontSize: 10.5, fontWeight: 600,
                padding: "5px 0", borderBottom: "1px solid var(--t2-border)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{humanize(r.key)}</div>
              <div style={{
                padding: "5px 0", borderBottom: "1px solid var(--t2-border)",
                minWidth: 0, overflow: "hidden",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <Value value={r.value} />
                {hasTypeIssue(r.flags) && (
                  <span title={r.flags.moneyString ? "Money stored as text (£/comma) — should be numeric" : "Date not in ISO 8601 — should be normalised"}
                    className="t2-mono" style={{
                      fontSize: 8.5, fontWeight: 700, letterSpacing: ".06em",
                      padding: "1px 5px", borderRadius: 999, whiteSpace: "nowrap",
                      color: r.flags.moneyString ? "#fca5a5" : "#fcd34d",
                      background: r.flags.moneyString ? "rgba(244,63,94,0.12)" : "rgba(245,158,11,0.12)",
                      border: `1px solid ${r.flags.moneyString ? "rgba(244,63,94,0.35)" : "rgba(245,158,11,0.35)"}`,
                    }}>{r.flags.moneyString ? "£ TEXT" : "NON-ISO"}</span>
                )}
              </div>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DataAudit({
  doc,
  skip = [],
  title = "Data audit",
  defaultOpen = false,
  testid = "data-audit",
}) {
  const [search, setSearch] = useState("");
  const [openTop, setOpenTop] = useState(defaultOpen);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const sections = useMemo(() => buildSections(doc || {}, skip), [doc, skip]);

  const stats = useMemo(() => {
    let scalar = 0, filled = 0, empty = 0, money = 0, date = 0;
    for (const s of sections) for (const r of s.rows) {
      scalar++;
      const f = r.flags || {};
      if (f.empty) empty++; else filled++;
      if (f.moneyString) money++;
      if (f.nonIsoDate) date++;
    }
    return { scalar, filled, empty, money, date, pct: scalar ? Math.round(100 * filled / scalar) : 0 };
  }, [sections]);

  if (!doc || typeof doc !== "object") return null;

  const totalFields = sections.reduce((n, s) => n + s.rows.length, 0);
  const pctColor = stats.pct >= 70 ? "var(--t2-ok)" : stats.pct >= 40 ? "#fcd34d" : "#fca5a5";

  const copyJson = () => {
    try { navigator.clipboard?.writeText(JSON.stringify(doc, null, 2)); }
    catch { /* silent */ }
  };

  const needle = (search || "").trim().toLowerCase();

  return (
    <div data-testid={testid} style={{ marginTop: 24 }}>
      <button
        onClick={() => setOpenTop((v) => !v)}
        data-testid={`${testid}-toggle`}
        style={{
          width: "100%", padding: "10px 14px",
          background: "var(--t2-surface)",
          border: "1px solid var(--t2-border)",
          borderRadius: 10,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: "pointer", textAlign: "left",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Database className="w-4 h-4" style={{ color: "var(--t2-orange)" }} />
          <span style={{ color: "var(--t2-white)", fontWeight: 700, fontSize: 14 }}>{title}</span>
          <span className="t2-mono" style={{
            color: "var(--t2-grey3)", fontSize: 10.5,
            padding: "2px 8px", background: "var(--t2-surface3)",
            borderRadius: 999, border: "1px solid var(--t2-border2)",
          }}>
            {totalFields} field{totalFields === 1 ? "" : "s"} · {sections.length} section{sections.length === 1 ? "" : "s"}
          </span>
          <span title="Completeness = filled scalar fields ÷ total scalar fields"
            className="t2-mono" style={{
              color: pctColor, fontSize: 10.5, fontWeight: 700,
              padding: "2px 8px", background: "var(--t2-surface3)",
              borderRadius: 999, border: `1px solid ${pctColor}44`,
            }}>
            {stats.pct}% complete · {stats.filled}/{stats.scalar}
          </span>
          {stats.money > 0 && (
            <span className="t2-mono" style={{
              color: "#fca5a5", fontSize: 10, fontWeight: 700, padding: "2px 8px",
              borderRadius: 999, background: "rgba(244,63,94,0.12)", border: "1px solid rgba(244,63,94,0.35)",
            }}>{stats.money} £-text</span>
          )}
          {stats.date > 0 && (
            <span className="t2-mono" style={{
              color: "#fcd34d", fontSize: 10, fontWeight: 700, padding: "2px 8px",
              borderRadius: 999, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)",
            }}>{stats.date} non-ISO</span>
          )}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--t2-grey3)" }}>
          <span className="t2-mono" style={{
            fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase",
          }}>{openTop ? "hide" : "show"}</span>
          {openTop ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </button>

      {openTop && (
        <div style={{
          marginTop: 8,
          background: "var(--t2-surface)",
          border: "1px solid var(--t2-border)",
          borderRadius: 10,
          padding: "14px 16px",
        }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
            <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
              <Search className="w-3.5 h-3.5" style={{
                position: "absolute", left: 10, top: "50%",
                transform: "translateY(-50%)", color: "var(--t2-grey3)",
              }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter fields (e.g. price, brand, sku)…"
                className="t2-input"
                style={{ paddingLeft: 30, width: "100%" }}
                data-testid={`${testid}-search`}
              />
            </div>
            <button
              onClick={() => setIssuesOnly((v) => !v)}
              className="t2-btn-ghost"
              data-testid={`${testid}-issues-toggle`}
              title="Show only fields with a type issue (£-text money / non-ISO dates)"
              style={{
                fontSize: 10.5,
                color: issuesOnly ? "#fca5a5" : undefined,
                borderColor: issuesOnly ? "rgba(244,63,94,0.4)" : undefined,
              }}
            >
              {issuesOnly ? "Issues only ✓" : "Issues only"}
            </button>
            <button
              onClick={copyJson}
              className="t2-btn-ghost"
              data-testid={`${testid}-copy`}
              title="Copy full JSON to clipboard"
              style={{ fontSize: 10.5 }}
            >
              <CopyIcon className="w-3.5 h-3.5" /> Copy JSON
            </button>
          </div>

          {sections.map((s, i) => (
            <Section key={s.path} section={s} needle={needle} issuesOnly={issuesOnly}
              defaultOpen={i < 3 || sections.length <= 3} />
          ))}
        </div>
      )}
    </div>
  );
}
